import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';

import { IPgnMove } from '@libs/chess/pgn';

import { UciEngine, IValuationLine } from './providers/engines';
import { Valuator, type IVariant as ValuatorVariant } from './Valuator';
import { IPreview, IVariant } from './Valuator';

const MAX_DEPTH = 99;
const DEFAULT_MOVETIME_SEC = 10;
const BACKGROUND_MOVETIME_MS = 100;

export type Props = {
  className?: string;
  fen: string;
  enabled: boolean;
  moveTimeSec?: number;
  preview?: IPreview;
  valuateEvenIfDisabled?: boolean;
  onToggle?: (enabled: boolean) => void;
  onValuation: (fen: string, v: IValuationLine[]) => void;
  onMoveClick?: (m: IPgnMove, v: ValuatorVariant) => void;
  onMoveHover?: (m: IPgnMove, v: IVariant) => void;
};

export const ValuatorWidget = memo(function PositionAnalyzerWidget({
  className,
  fen,
  enabled,
  moveTimeSec = DEFAULT_MOVETIME_SEC,
  preview,
  valuateEvenIfDisabled,
  onToggle,
  onValuation,
  onMoveClick,
  onMoveHover,
}: Props) {
  const specs = useMemo(() => UciEngine.getFirstSupported(), []);
  const engine = useMemo(() => {
    try {
      return enabled || !valuateEvenIfDisabled ? UciEngine.create(specs) : UciEngine.createLite();
    } catch (e) {
      console.error(e);
    }
  }, [enabled, valuateEvenIfDisabled, specs]);
  const [variants, setVariants] = useState<ValuatorVariant[] | undefined>();
  const [expanded, setExpanded] = useState(false);
  const [pending, setPending] = useState(!!fen);
  const [thinking, setThinking] = useState(false);
  const [isError, setIsError] = useState(false);
  const lastIdRef = useRef(0);
  const multiPV = expanded ? 3 : 1; // кол-во отображаемых независимых линий

  // искусственная индикация смены обрабатываемого хода
  useEffect(() => {
    setThinking(true);
    const id = setTimeout(() => setThinking(false), 300);
    return () => clearTimeout(id);
  }, [fen]);

  // Переменные для доступа из эффектов и коллбэков
  const _store = { expanded, fen, multiPV, onValuation };
  const storeRef = useRef(_store);
  storeRef.current = _store;

  // Подписка на события движка
  useEffect(() => {
    if (!engine) return;

    const unsubscribe = engine.subscribe((e) => {
      if (e.type === 'initialized') return;

      if (e.type === 'error') {
        setIsError(true);
        setVariants([]);
        setPending(false);
        return;
      }

      const vals = e.valuations;
      try {
        const vars: ValuatorVariant[] = vals.map((v) => {
          const chess = new Chess(v.fen, {
            enable960: true,
          });
          return {
            type: v.type,
            depth: v.depth,
            valuation: v.valuation,
            moves: v.moves.map((m) => {
              const moveFull = chess.moveNumber();
              const cm = chess.move(m);
              return {
                id: `analyzer-${lastIdRef.current++}`,
                moveFull,
                color: cm.color === 'w' ? 'white' : 'black',
                san: cm.san,
              } satisfies IPgnMove;
            }),
          };
        });
        setVariants(vars);
      } catch {
        setVariants([]);
      }
      setPending(e.type === 'progress');
      storeRef.current.onValuation(e.req.fen, vals);
    });

    return () => {
      unsubscribe();
      engine.terminate();
    };
  }, [engine]);

  useEffect(() => {
    if (!enabled && valuateEvenIfDisabled) {
      engine?.evaluate({
        fen,
        depth: MAX_DEPTH,
        movetime: enabled ? moveTimeSec * 1000 : BACKGROUND_MOVETIME_MS,
        multiPV: 1,
      });
    } else if (enabled) {
      setIsError(false);
      engine?.evaluate({
        fen,
        depth: MAX_DEPTH,
        movetime: moveTimeSec * 1000,
        multiPV,
      });
    } else {
      engine?.stop();
    }
  }, [engine, fen, enabled, moveTimeSec, multiPV, valuateEvenIfDisabled]);

  return (
    <Valuator
      className={className}
      title={specs.name}
      enabled={enabled}
      expanded={expanded}
      thinking={thinking}
      variants={variants}
      depth={variants?.[0]?.depth}
      pending={pending}
      preview={preview}
      onToggle={onToggle}
      onExpand={setExpanded}
      onMoveClick={onMoveClick}
      onMoveHover={onMoveHover}
      isError={isError}
    />
  );
});
